import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, DownloadSimple, Boat, Cube, ArrowRight, Timer, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { sealsText } from "@/lib/seals";
import LiveTracking from "@/components/LiveTracking";
import VesselMap from "@/components/VesselMap";

function FreeChip({ label, info }) {
  const d = info.days_left;
  const over = d < 0;
  const warn = d >= 0 && d <= 3;
  const tone = over ? "border-rose-200 bg-rose-50 text-rose-700" : warn ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700";
  const txt = over ? `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} overdue` : d === 0 ? "Last free day is today" : `${d} day${d > 1 ? "s" : ""} left`;
  return (
    <div className={`rounded-lg border p-3 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-lg font-bold mt-0.5">{txt}</div>
      <div className="text-[11px] opacity-80">Last date: {info.date}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</div>
      <div className="text-slate-800 text-sm mt-0.5">{v || "—"}</div>
    </div>
  );
}

export default function PortalShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blBusy, setBlBusy] = useState(false);

  const fetchVesselPosition = React.useCallback(
    () => portalApi.get(`/portal/shipments/${id}/vessel-position`).then((r) => r.data),
    [id]
  );

  const reload = async () => {
    try {
      const [j, d] = await Promise.all([
        portalApi.get(`/portal/shipments/${id}`),
        portalApi.get(`/portal/shipments/${id}/documents`).catch(() => ({ data: [] })),
      ]);
      setJob(j.data); setDocs(d.data || []);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const downloadDraftBL = async () => {
    try {
      const { data } = await portalApi.get(`/portal/shipments/${id}/draft-bl`);
      const a = document.createElement("a");
      a.href = data.file_data.startsWith("data:") ? data.file_data : `data:${data.file_type || "application/octet-stream"};base64,${data.file_data}`;
      a.download = data.file_name || `draft-bl-${job.job_number || id}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const approveDraftBL = async () => {
    if (!window.confirm("Approve this draft Bill of Lading? Confirm all details are correct.")) return;
    setBlBusy(true);
    try { await portalApi.post(`/portal/shipments/${id}/draft-bl/approve`); toast.success("Draft B/L approved. Thank you!"); await reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBlBusy(false); }
  };
  const amendDraftBL = async () => {
    const remarks = window.prompt("Briefly, what needs to change? (optional)\nYou'll be taken to your Shipping Instruction to edit the details, then resubmit for a revised draft B/L.", "");
    if (remarks === null) return;
    setBlBusy(true);
    try {
      await portalApi.post(`/portal/shipments/${id}/draft-bl/amend`, { remarks });
      toast.success("Opening your Shipping Instruction to amend…");
      navigate(`/portal/shipments/${id}/si`);
    } catch (e) { toast.error(formatApiError(e)); setBlBusy(false); }
  };

  const downloadAllDocs = async () => {
    try {
      const { data } = await portalApi.get(`/portal/shipments/${id}/documents/zip`);
      const a = document.createElement("a");
      a.href = data.file_data;
      a.download = data.file_name || `${job.job_number || id}-documents.zip`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (doc) => {
    try {
      const { data } = await portalApi.get(`/portal/documents/${doc.id}/file`);
      const a = document.createElement("a");
      a.href = data.file_data.startsWith("data:") ? data.file_data : `data:${data.file_type || "application/octet-stream"};base64,${data.file_data}`;
      a.download = data.file_name || "document";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest py-10 text-center">Loading…</div>;
  if (!job) return <div className="text-slate-500 py-10 text-center">Shipment not found.</div>;

  const card = "bg-white border border-slate-200 rounded-xl shadow-sm";

  return (
    <div className="space-y-5">
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-700"><ArrowLeft size={15} /> All shipments</Link>

      <div className={`${card} p-5`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono font-semibold text-slate-900 text-lg">{job.job_number}</span>
              <span className="text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-0.5 border border-blue-200 bg-blue-50 text-blue-700">{job.status}</span>
            </div>
            <div className="text-slate-600 mt-1.5 inline-flex items-center gap-2 text-sm"><Boat size={15} className="text-slate-400" /> {job.pol_name || "—"} <ArrowRight size={13} className="text-slate-400" /> {job.pod_name || "—"} {job.carrier_name ? <span className="text-slate-400">· {job.carrier_name}</span> : null}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {job.job_type === "Export" && (
              <button
                onClick={() => navigate(`/portal/shipments/${job.id}/si`)}
                title={job.si_locked_reason || undefined}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-semibold ${
                  job.si_locked
                    ? "border border-slate-300 text-slate-600 hover:bg-slate-50"
                    : "bg-blue-700 hover:bg-blue-800 text-white"}`}>
                {/* Only invite a submission while the SI is genuinely still open.
                    si_locked also covers staff having lodged it or raised the B/L —
                    the draft's own status alone used to offer "Submit" after the
                    B/L was already issued. */}
                <FileText size={14} /> {job.si_locked
                  ? (job.si_draft_status === "submitted" ? "View submitted SI" : "View shipping instructions")
                  : job.si_draft_status === "accepted" ? "View SI" : "Submit Shipping Instruction"}
              </button>
            )}
            <button
              onClick={() => navigate("/portal/bookings", { state: { prefill: { job_type: job.job_type, mode: job.mode, pol_name: job.pol_name, pod_name: job.pod_name, commodity: job.description_of_goods } } })}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-medium">
              <ArrowsClockwise size={14} /> Book this route again
            </button>
          </div>
        </div>
      </div>

      {/* Draft B/L review & approval */}
      {job.draft_bl_copy?.document_id && ["Sent", "Approved", "Rejected"].includes(job.draft_bl_status) && (
        <div className={`${card} p-5 ${job.draft_bl_status === "Sent" ? "border-blue-200" : ""}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-2.5">
              <FileText size={18} className="text-blue-600 mt-0.5" />
              <div>
                <div className="text-slate-900 font-semibold">
                  {job.draft_bl_status === "Approved" ? "Draft B/L approved" : job.draft_bl_status === "Rejected" ? "Changes requested on the draft B/L" : "Draft Bill of Lading — please review"}
                  {job.draft_bl_portal_version ? <span className="ml-2 text-[11px] font-medium text-slate-400">v{job.draft_bl_portal_version}</span> : null}
                </div>
                <div className="text-sm text-slate-500 mt-0.5">
                  {job.draft_bl_status === "Sent"
                    ? "Download the draft B/L, check every detail, then Approve or request changes."
                    : job.draft_bl_status === "Approved"
                    ? "Thank you — you've approved this draft B/L."
                    : "We've received your remarks and will send a revised draft."}
                </div>
                {job.draft_bl_status === "Rejected" && job.draft_bl_rejection_remarks && (
                  <div className="text-xs text-amber-700 mt-1.5">Your remarks: {job.draft_bl_rejection_remarks}</div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <button onClick={downloadDraftBL} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium">
              <DownloadSimple size={15} /> Download draft B/L
            </button>
            {job.draft_bl_status === "Sent" && (
              <>
                <button onClick={approveDraftBL} disabled={blBusy} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
                  <CheckCircle size={16} weight="bold" /> Approve
                </button>
                <button onClick={amendDraftBL} disabled={blBusy} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-semibold disabled:opacity-50">
                  <ArrowsClockwise size={16} /> Amend details
                </button>
              </>
            )}
            {job.draft_bl_status === "Approved" && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-medium"><CheckCircle size={16} weight="fill" /> Approved</span>}
          </div>
        </div>
      )}

      <LiveTracking
        theme="light"
        status={job.status}
        pol={job.pol_name}
        pod={job.pod_name}
        etd={job.etd}
        eta={job.eta}
        sob={job.shipped_on_board_date}
        vessel={job.vessel_name}
        voyage={job.voyage_number}
        carrier={job.carrier_name}
        mode={job.mode}
        legs={job.transhipments || []}
        updatedAt={job.updated_at}
      />

      {(job.mode || "").toLowerCase() !== "air" && (
        <VesselMap fetcher={fetchVesselPosition} height={300} theme="light" />
      )}

      {/* Milestone timeline */}
      {(job.timeline || []).length > 0 && (
        <div className={`${card} p-5`}>
          <div className="text-sm font-semibold text-slate-900 mb-4 inline-flex items-center gap-2"><Boat size={16} className="text-blue-600" /> Shipment milestones</div>
          <ol className="relative border-l-2 border-slate-100 ml-2">
            {job.timeline.map((t, i) => (
              <li key={i} className="ml-4 pb-4 last:pb-0">
                <span className={`absolute -left-[7px] mt-1 w-3 h-3 rounded-full ${t.done ? "bg-emerald-500" : "bg-slate-300"}`} />
                <div className="flex items-center justify-between gap-3">
                  <div className={`text-sm ${t.done ? "text-slate-800 font-medium" : "text-slate-500"}`}>
                    {t.stage}{t.location ? <span className="text-slate-400 font-normal"> · {t.location}</span> : null}
                  </div>
                  <div className="text-xs text-slate-400 font-mono shrink-0">{t.date || "—"}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {job.free_time && (
        <div className={`${card} p-5`}>
          <div className="text-sm font-semibold text-slate-900 mb-3 inline-flex items-center gap-2"><Timer size={16} className="text-blue-600" /> Free time &amp; demurrage</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {job.free_time.free_time && <FreeChip label="Container free time" info={job.free_time.free_time} />}
            {job.free_time.do_validity && <FreeChip label="Delivery Order validity" info={job.free_time.do_validity} />}
          </div>
          <div className="text-[11px] text-slate-400 mt-3">After the last free date, demurrage / detention charges may apply as per your agreement. Contact us to arrange pickup or an extension.</div>
        </div>
      )}

      <div className={`${card} p-5 grid grid-cols-2 md:grid-cols-3 gap-4`}>
        <KV k="Type · Mode" v={`${job.job_type || ""} · ${job.mode || ""}`} />
        <KV k="Vessel / Voyage" v={[job.vessel_name, job.voyage_number].filter(Boolean).join(" / ")} />
        <KV k="Flight" v={job.flight_number} />
        <KV k="ETD" v={(job.etd || "").slice(0, 10)} />
        <KV k="ETA" v={(job.eta || "").slice(0, 10)} />
        <KV k="Shipped On Board" v={(job.shipped_on_board_date || "").slice(0, 10)} />
        <KV k="HBL" v={job.hbl_number} />
        <KV k="AWB" v={job.awb_number} />
        <KV k="Booking #" v={job.booking_number} />
        <KV k="Shipper" v={job.shipper_snapshot?.name} />
        <KV k="Consignee" v={job.consignee_snapshot?.name} />
        <KV k="Place of Delivery" v={job.place_of_delivery} />
        <KV k="Goods" v={job.description_of_goods} />
      </div>

      {/* Transhipment legs */}
      {(job.transhipments || []).length > 0 && (
        <div className={`${card} overflow-hidden`}>
          <div className="px-4 py-3 border-b border-slate-200 text-sm text-slate-900 font-semibold inline-flex items-center gap-2"><Boat size={15} className="text-blue-600" /> Transhipment ({job.transhipments.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Transhipment Port</th>
                  <th className="px-4 py-2 text-left">Connecting Vessel / Voyage</th>
                  <th className="px-4 py-2 text-left">Arrival (ETA)</th>
                  <th className="px-4 py-2 text-left">Departure (ETD)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {job.transhipments.map((t, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-800">{t.port_name || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{[t.vessel_name, t.voyage_number].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-4 py-2 font-mono text-slate-600">{(t.eta || "").slice(0, 10) || "—"}</td>
                    <td className="px-4 py-2 font-mono text-slate-600">{(t.etd || "").slice(0, 10) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Containers */}
      {(job.containers || []).length > 0 && (
        <div className={`${card} overflow-hidden`}>
          <div className="px-4 py-3 border-b border-slate-200 text-sm text-slate-900 font-semibold inline-flex items-center gap-2"><Cube size={15} className="text-blue-600" /> Containers ({job.containers.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-2 text-left">Container #</th><th className="px-4 py-2 text-left">Type</th><th className="px-4 py-2 text-left">Seal</th><th className="px-4 py-2 text-left">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {job.containers.map((c, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-slate-800">{c.container_number || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{c.equipment_code || "—"}</td>
                    <td className="px-4 py-2 font-mono text-slate-500 text-xs">{sealsText(c) || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{c.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Documents */}
      <div className={`${card} overflow-hidden`}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-900 font-semibold inline-flex items-center gap-2"><FileText size={15} className="text-blue-600" /> Documents ({docs.length})</div>
          {docs.some((d) => d.has_file) && (
            <button onClick={downloadAllDocs} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-xs font-medium" data-testid="download-all-docs">
              <DownloadSimple size={14} weight="bold" /> Download all (zip)
            </button>
          )}
        </div>
        {docs.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-400 text-sm">No documents available yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {docs.map((d) => (
              <div key={d.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-slate-800 text-sm truncate">{d.name || d.doc_type || d.file_name || "Document"}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{[d.doc_type, d.reference_number, (d.uploaded_at || "").slice(0, 10)].filter(Boolean).join(" · ")}</div>
                </div>
                {d.has_file ? (
                  <button onClick={() => download(d)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 text-xs"><DownloadSimple size={14} /> Download</button>
                ) : d.file_url ? (
                  <a href={d.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-600 hover:bg-blue-50 hover:text-blue-700 text-xs"><DownloadSimple size={14} /> Open</a>
                ) : (
                  <span className="text-slate-400 text-xs">—</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
